import React, { useState, useEffect, useMemo } from 'react'; // Import useMemo
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Icon } from '@iconify/react/dist/iconify.js';
import api from '../../services/api';
// Removed useNavigate as it wasn't used directly in the provided snippet
// import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ViewApprovalRequestModal from './ViewApprovalRequestModal';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

const ApprovalRequestsTable = () => {
  const [data, setData] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [selectedResourceType, setSelectedResourceType] = useState('');
  const [currentRequest, setCurrentRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  // Removed navigate as it wasn't used directly in the provided snippet
  // const navigate = useNavigate();

  // Fetch approval requests from the API
  const fetchApprovalRequests = async () => {
    try {
      const response = await api.get('/approvals/');
      setData(response.data.data || []); // Ensure data is always an array
    } catch (error) {
      console.error('Error fetching approval requests:', error);
      toast.error('Failed to load approval requests.'); // User feedback
    }
  };

  useEffect(() => {
    fetchApprovalRequests();
  }, []);

  // Convert approvable_type to a user-friendly name
  const resourceTypeName = (row) => {
    if (!row) return 'N/A'; // Handle potential undefined row
    // Use optional chaining for safety if row exists but type doesn't
    switch (row.approvable_type) {
      case 'App\\Models\\Project': return 'Project';
      case 'App\\Models\\Unit': return 'Unit';
      case 'App\\Models\\PaymentPlan': return 'Payment Plan';
      // Assuming anything else is Developer based on the original logic
      default: return 'Developer';
    }
  };

  // Define table columns dynamically using useMemo
  const columns = useMemo(() => {
    // Define base columns that are always present
    const baseColumns = [
      {
        name: 'ID',
        selector: (row) => row?.id ?? 'N/A', // Optional chaining and nullish coalescing
        sortable: true,
        maxWidth: '80px',
      },
      // Developer related columns will be inserted here conditionally
      {
        name: 'Resource Type',
        selector: (row) => resourceTypeName(row),
        sortable: true,
        // Removed maxWidth to allow grow to work better if needed, adjust as necessary
        // maxWidth: '80px',
        grow: 1, // Adjusted grow slightly
      },
      {
        name: 'New/Update',
        selector: (row) => (row?.original_data ? 'Update' : 'New'),
        sortable: false, // Sorting might not make sense here
        maxWidth: '100px', // Slightly wider for text
        // grow: 1, // Removed grow as maxWidth is set
      },
      {
        name: 'Actions',
        cell: (row) => (
          <div className="d-flex">
            <button
              onClick={() => {
                setCurrentRequest(row);
                setShowViewModal(true);
              }}
              title="View"
              className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0" // Added border-0 for consistency
            >
              <Icon icon="iconamoon:eye-light" />
            </button>
            <button
              onClick={async () => {
                if (!row?.id) return; // Safety check
                try {
                  await api.post(`/approvals/${row.id}/approve`);
                  toast.success('Request Approved Successfully!');
                  fetchApprovalRequests(); // Refresh data
                } catch (error) {
                  console.error("Approval error:", error); // Log error details
                  toast.error(error?.response?.data?.message || 'Failed to Approve Request'); // Show specific error if available
                }
              }}
              title="Approve"
              className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0" // Added border-0
            >
              <Icon icon="mdi:check-circle-outline" />
            </button>
            <button
              onClick={() => {
                setCurrentRequest(row);
                setShowRejectModal(true);
              }}
              title="Decline"
              className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0" // Added border-0
            >
              <Icon icon="mdi:close-circle-outline" />
            </button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        minWidth: '150px', // Ensure enough space for buttons
        grow: 0, // Don't let actions grow excessively
      },
    ];

    // Define the conditional columns
    const developerColumns = [
      // Note: Removed the duplicate Developer Name column
      {
        name: 'Developer Name',
        // Use optional chaining extensively for safety
        selector: (row) => row?.developer_sales?.developer?.name ?? 'N/A',
        sortable: true,
        // Removed maxWidth, let it adjust or set a minWidth if needed
        // maxWidth: '120px',
        minWidth: '150px',
        grow: 1,
      },
      {
        name: 'Dev Email',
        selector: (row) => row?.developer_sales?.developer?.email ?? 'N/A',
        sortable: true,
        // Removed maxWidth
        // maxWidth: '500px',
        minWidth: '200px', // Set a minWidth
        grow: 2, // Allow more growth for email
      },
      {
        name: 'Sales Rep', // Renamed for clarity
        selector: (row) => row?.developer_sales?.name ?? 'N/A',
        sortable: true,
        // Removed maxWidth
        // maxWidth: '200px',
        minWidth: '150px',
        grow: 1,
      },
    ];

    // Check if the developer columns should be shown
    // Show if 'All' is selected OR if 'Project' or 'Unit' is specifically selected
    const showDeveloperColumns =
      selectedResourceType === '' ||
      selectedResourceType === 'Project' ||
      selectedResourceType === 'Unit';

    if (showDeveloperColumns) {
      // Insert developer columns after the 'ID' column (index 1)
      baseColumns.splice(1, 0, ...developerColumns);
    }

    return baseColumns;
  }, [selectedResourceType]); // Recalculate columns only when selectedResourceType changes

  // Build unique resource type options for the dropdown
  // Use useMemo for minor optimization if data array is large
  const resourceTypes = useMemo(() =>
    Array.from(new Set(data.map((item) => resourceTypeName(item))))
    , [data]);

  // Filter the data based on text search and selected resource type
  // Use useMemo for optimization
  const filteredData = useMemo(() =>
    data.filter((item) => {
      // Ensure item and necessary fields exist before trying to access them
      const idMatch = String(item?.id ?? '').includes(filterText);
      const textMatch = filterText
        ? idMatch || // Keep searching by ID
        (item?.developer_sales?.developer?.name ?? '').toLowerCase().includes(filterText.toLowerCase()) || // Add search by Dev Name
        (item?.developer_sales?.developer?.email ?? '').toLowerCase().includes(filterText.toLowerCase()) || // Add search by Dev Email
        (item?.developer_sales?.name ?? '').toLowerCase().includes(filterText.toLowerCase()) // Add search by Sales Rep
        : true; // If no filter text, always true

      const resourceMatch = selectedResourceType
        ? resourceTypeName(item) === selectedResourceType
        : true; // If no resource type selected, always true

      return textMatch && resourceMatch;
    })
    , [data, filterText, selectedResourceType]); // Recalculate when data, filterText, or selectedResourceType changes


  // Define CSV Headers dynamically based on visible columns
  const csvHeaders = useMemo(() => {
    // Start with common headers
    const headers = [
      { label: 'ID', key: 'id' },
    ];

    // Conditionally add developer headers
    const showDeveloperColumns =
      selectedResourceType === '' ||
      selectedResourceType === 'Project' ||
      selectedResourceType === 'Unit';

    if (showDeveloperColumns) {
      headers.push(
        { label: 'Developer Name', key: 'developer_sales.developer.name' }, // Use dot notation for nested keys
        { label: 'Developer Email', key: 'developer_sales.developer.email' },
        { label: 'Sales Rep', key: 'developer_sales.name' }
      );
    }

    // Add remaining common headers
    headers.push(
      { label: 'Resource Type', key: 'approvable_type' }, // You might want to map this via resourceTypeName before export if needed
      { label: 'Status', key: 'original_data', format: (value) => value ? 'Update' : 'New' } // Format the output for New/Update
    );

    return headers;
  }, [selectedResourceType]); // Recalculate headers when the filter changes


  // Prepare data for CSV export, handling nested structures
  const csvData = useMemo(() => {
    return filteredData.map(item => {
      const baseData = {
        id: item?.id ?? '',
        approvable_type: resourceTypeName(item), // Use the formatted name
        original_data: item?.original_data // Keep raw value for header formatting logic
      };

      const showDeveloperColumns =
        selectedResourceType === '' ||
        selectedResourceType === 'Project' ||
        selectedResourceType === 'Unit';

      if (showDeveloperColumns) {
        return {
          ...baseData,
          'developer_sales.developer.name': item?.developer_sales?.developer?.name ?? '', // Match header key
          'developer_sales.developer.email': item?.developer_sales?.developer?.email ?? '', // Match header key
          'developer_sales.name': item?.developer_sales?.name ?? '', // Match header key
        }
      }
      return baseData;
    });
  }, [filteredData, selectedResourceType]); // Recalculate when filtered data or selection changes


  const handleReject = async () => {
    if (!currentRequest?.id) {
      toast.warn('No request selected.');
      return;
    }
    if (!rejectionReason.trim()) { // Basic validation
      toast.warn('Please enter a rejection reason.');
      return;
    }
    try {
      await api.post(`/approvals/${currentRequest.id}/reject`, {
        reason: rejectionReason,
      });
      toast.success('Request Declined Successfully!');
      fetchApprovalRequests(); // Refresh data
      setShowRejectModal(false);
      setRejectionReason(''); // Reset reason
      setCurrentRequest(null); // Reset current request
    } catch (error) {
      console.error("Rejection error:", error); // Log error details
      toast.error(error?.response?.data?.message || 'Failed to Decline Request'); // Show specific error if available
    }
  };

  return (
    <div className="card basic-data-table px-3 py-3">
      <div className="card-body">
        {/* Filters and Export */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2"> {/* Added flex-wrap and gap */}
          <div className="d-flex flex-wrap gap-2"> {/* Added flex-wrap and gap */}
            <input
              type="text"
              placeholder="Search..." // Generic Search Placeholder
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="form-control"
              style={{ width: '200px' }} // Maintain fixed width or use Bootstrap classes
            />
            <select
              value={selectedResourceType}
              onChange={(e) => setSelectedResourceType(e.target.value)}
              className="form-select" // Use form-select for Bootstrap 5 styling
              style={{ width: '200px' }} // Maintain fixed width or use Bootstrap classes
              aria-label="Select Resource Type" // Accessibility
            >
              <option value="">All Resource Types</option>
              {resourceTypes.map((rt) => (
                <option key={rt} value={rt}>
                  {rt}
                </option>
              ))}
            </select>
          </div>
          <CSVLink
            data={csvData} // Use prepared CSV data
            headers={csvHeaders} // Use dynamic headers
            filename={`approval-requests-${selectedResourceType || 'all'}.csv`} // Dynamic filename
            className="btn btn-secondary"
            target="_blank" // Good practice for download links
          >
            Export CSV
          </CSVLink>
        </div>

        {/* DataTable */}
        <div className="table-responsive"> {/* Wrap table for responsiveness */}
          <DataTable
            columns={columns} // Use the memoized dynamic columns
            data={filteredData} // Use the memoized filtered data
            pagination
            highlightOnHover
            responsive // Keep this, but wrapping helps too
            striped
            noDataComponent={<div className="p-4 text-center">No approval requests found.</div>} // Custom message
            paginationComponentOptions={{
              rowsPerPageText: 'Rows per page:',
              rangeSeparatorText: 'of',
              selectAllRowsItem: true,
              selectAllRowsItemText: 'All',
            }}
          />
        </div>
      </div>

      {/* View Modal */}
      {showViewModal && currentRequest && ( // Ensure currentRequest exists before rendering
        <ViewApprovalRequestModal
          show={showViewModal}
          handleClose={() => {
            setShowViewModal(false);
            setCurrentRequest(null); // Reset current request on close
            // fetchApprovalRequests(); // Fetching is now done after approve/reject actions
          }}
          approvalRequest={currentRequest}
        />
      )}

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered> {/* Added centered */}
        <Modal.Header closeButton>
          <Modal.Title>Reject Approval Request #{currentRequest?.id}</Modal.Title> {/* Show ID */}
        </Modal.Header>
        <Modal.Body>
          <p>Please provide a reason for rejecting this request:</p>
          {/* Use a textarea with proper label */}
          <label htmlFor="rejectionReason" className="form-label visually-hidden">Rejection Reason</label>
          <textarea
            id="rejectionReason" // Match label htmlFor
            className="form-control"
            placeholder="Enter rejection reason (required)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={3} // Give it some height
            required // HTML5 validation hint
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowRejectModal(false);
            setRejectionReason(''); // Clear reason on cancel
          }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleReject} disabled={!rejectionReason.trim()}> {/* Disable if reason is empty */}
            Confirm Rejection
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ApprovalRequestsTable;